<template>
  <div class="ps-page">
    <ps-top-bar :back-label="$t('faq.back') || $t('contact.back')" />

    <main class="wrap faq-main">
      <div class="faq-head">
        <p class="ps-eyebrow">{{ $t("faq.label") || "FAQ" }}</p>
        <h1 class="ps-title">{{ $t("faq_title") }}</h1>
        <p class="faq-head__sub">{{ $t("faq_subtitle") }}</p>
      </div>

      <!-- search -->
      <div class="faq-search">
        <v-icon size="16" color="#64748b" class="faq-search__icon"
          >fal fa-search</v-icon
        >
        <input
          v-model="searchQuery"
          type="text"
          :placeholder="$t('faq_search_placeholder')"
          class="ps-input faq-search__input"
        />
      </div>

      <!-- accordion sections -->
      <div class="faq-list">
        <div
          v-for="(section, si) in filteredSections"
          :key="section.titleKey"
          class="faq-card"
        >
          <button
            class="faq-card__head"
            :aria-expanded="openPanels.includes(si)"
            @click="toggle(si)"
          >
            <span class="faq-card__icon"
              ><v-icon size="18">{{ section.icon }}</v-icon></span
            >
            <span class="faq-card__title">{{ $t(section.titleKey) }}</span>
            <v-icon
              size="16"
              class="faq-card__chevron"
              :class="{ 'faq-card__chevron--open': openPanels.includes(si) }"
              >fal fa-chevron-down</v-icon
            >
          </button>

          <div v-show="openPanels.includes(si)" class="faq-card__body">
            <div
              v-for="(block, bi) in section.blocks"
              :key="bi"
              class="faq-block"
            >
              <h3 class="faq-block__title">{{ $t(block.titleKey) }}</h3>
              <p v-if="block.descKey" class="faq-block__desc">
                {{ $t(block.descKey) }}
              </p>
              <ul class="faq-block__list">
                <li v-for="(item, ii) in block.items" :key="ii" class="faq-block__item">
                  <span class="faq-block__dot"></span>
                  <span>
                    {{ $t(item.key) }}
                    <span
                      v-if="item.description"
                      class="faq-block__item-desc"
                      >{{ $t(item.description) }}</span
                    >
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <p v-if="!filteredSections.length" class="faq-empty">
          {{ $t("faq_no_results") || "No results" }}
        </p>
      </div>

      <div class="faq-contact">
        <router-link to="/contact_us" class="ps-btn ps-btn--brand">{{
          $t("faq_contact_us_button")
        }}</router-link>
      </div>
    </main>
  </div>
</template>

<script>
import PsTopBar from "@/components/global/PsTopBar.vue";

export default {
  name: "FAQ",
  layout: "blank",
  components: { PsTopBar },
  data() {
    return {
      searchQuery: "",
      openPanels: [],
      businessTypes: [
        { key: "faq_business_type_minimart", description: "faq_business_type_minimart_desc" },
        { key: "faq_business_type_restaurant", description: "faq_business_type_restaurant_desc" },
        { key: "faq_business_type_pharmacy", description: "faq_business_type_pharmacy_desc" },
        { key: "faq_business_type_clothing", description: "faq_business_type_clothing_desc" },
        { key: "faq_business_type_convenience", description: "faq_business_type_convenience_desc" },
        { key: "faq_business_type_department", description: "faq_business_type_department_desc" },
        { key: "faq_business_type_bakery", description: "faq_business_type_bakery_desc" },
        { key: "faq_business_type_beauty", description: "faq_business_type_beauty_desc" },
        { key: "faq_business_type_hardware", description: "faq_business_type_hardware_desc" },
        { key: "faq_business_type_electronics", description: "faq_business_type_electronics_desc" },
        { key: "faq_business_type_gift", description: "faq_business_type_gift_desc" },
        { key: "faq_business_type_wholesale", description: "faq_business_type_wholesale_desc" },
      ],
      businessStages: [
        { key: "faq_business_stage_planning" },
        { key: "faq_business_stage_new" },
        { key: "faq_business_stage_growing" },
        { key: "faq_business_stage_established" },
        { key: "faq_business_stage_mature" },
      ],
      businessHours: [
        { key: "faq_hours_standard" },
        { key: "faq_hours_extended" },
        { key: "faq_hours_24_7" },
        { key: "faq_hours_seasonal" },
        { key: "faq_hours_weekends" },
      ],
      productVolumes: [
        { key: "faq_volume_micro" },
        { key: "faq_volume_small" },
        { key: "faq_volume_medium" },
        { key: "faq_volume_large" },
        { key: "faq_volume_very_large" },
        { key: "faq_volume_enterprise" },
      ],
      transactionVolumes: [
        { key: "faq_txn_volume_low" },
        { key: "faq_txn_volume_medium" },
        { key: "faq_txn_volume_high" },
        { key: "faq_txn_volume_very_high" },
        { key: "faq_txn_volume_enterprise" },
      ],
      transactionValues: [
        { key: "faq_txn_value_under_50k" },
        { key: "faq_txn_value_50k_200k" },
        { key: "faq_txn_value_200k_500k" },
        { key: "faq_txn_value_500k_1m" },
        { key: "faq_txn_value_over_1m" },
      ],
      revenueRanges: [
        { key: "faq_revenue_startup" },
        { key: "faq_revenue_small" },
        { key: "faq_revenue_medium" },
        { key: "faq_revenue_large" },
        { key: "faq_revenue_enterprise" },
      ],
      teamSizes: [
        { key: "faq_team_solo" },
        { key: "faq_team_micro" },
        { key: "faq_team_small" },
        { key: "faq_team_medium" },
        { key: "faq_team_large" },
        { key: "faq_team_enterprise" },
      ],
      locations: [
        { key: "faq_location_single" },
        { key: "faq_location_expanding" },
        { key: "faq_location_multi" },
        { key: "faq_location_regional" },
        { key: "faq_location_national" },
      ],
      technicalLevels: [
        { key: "faq_tech_beginner" },
        { key: "faq_tech_basic" },
        { key: "faq_tech_intermediate" },
        { key: "faq_tech_advanced" },
      ],
      connectivityOptions: [
        { key: "faq_connectivity_stable" },
        { key: "faq_connectivity_reliable" },
        { key: "faq_connectivity_intermittent" },
        { key: "faq_connectivity_unreliable" },
        { key: "faq_connectivity_offline" },
      ],
      hardwareOptions: [
        { key: "faq_hardware_desktop" },
        { key: "faq_hardware_laptop" },
        { key: "faq_hardware_tablet" },
        { key: "faq_hardware_smartphone" },
        { key: "faq_hardware_printer_58mm" },
        { key: "faq_hardware_printer_80mm" },
        { key: "faq_hardware_scanner" },
        { key: "faq_hardware_barcode_scanner" },
        { key: "faq_hardware_cash_drawer" },
        { key: "faq_hardware_kds" },
        { key: "faq_hardware_customer_display" },
        { key: "faq_hardware_none" },
      ],
      platformOptions: [
        { key: "faq_platform_web" },
        { key: "faq_platform_android" },
        { key: "faq_platform_ios" },
        { key: "faq_platform_desktop" },
        { key: "faq_platform_multiple" },
      ],
      budgetOptions: [
        { key: "faq_budget_have_all" },
        { key: "faq_budget_under_5m" },
        { key: "faq_budget_5m_10m" },
        { key: "faq_budget_10m_20m" },
        { key: "faq_budget_over_20m" },
        { key: "faq_budget_financing" },
      ],
      challenges: [
        { key: "faq_challenge_slow_checkout" },
        { key: "faq_challenge_inventory" },
        { key: "faq_challenge_stock" },
        { key: "faq_challenge_visibility" },
        { key: "faq_challenge_employee" },
        { key: "faq_challenge_cash" },
        { key: "faq_challenge_errors" },
        { key: "faq_challenge_remote" },
        { key: "faq_challenge_customer_data" },
        { key: "faq_challenge_multiple_locations" },
        { key: "faq_challenge_suppliers" },
        { key: "faq_challenge_variants" },
        { key: "faq_challenge_expiration" },
      ],
      recordKeepingMethods: [
        { key: "faq_record_none" },
        { key: "faq_record_handwritten" },
        { key: "faq_record_excel" },
        { key: "faq_record_calculator" },
        { key: "faq_record_existing_pos" },
        { key: "faq_record_accounting" },
        { key: "faq_record_mix" },
      ],
      posExperiences: [
        { key: "faq_pos_never" },
        { key: "faq_pos_too_complicated" },
        { key: "faq_pos_dissatisfied" },
        { key: "faq_pos_satisfied" },
        { key: "faq_pos_experienced" },
        { key: "faq_pos_tech_savvy" },
      ],
      seekingReasons: [
        { key: "faq_reason_expensive" },
        { key: "faq_reason_features" },
        { key: "faq_reason_support" },
        { key: "faq_reason_reliability" },
        { key: "faq_reason_scale" },
        { key: "faq_reason_cloud" },
        { key: "faq_reason_offline" },
        { key: "faq_reason_reporting" },
      ],
      essentialFeatures: [
        { key: "faq_feature_basic_sales" },
        { key: "faq_feature_inventory" },
        { key: "faq_feature_barcode" },
        { key: "faq_feature_receipt" },
        { key: "faq_feature_payment_methods" },
        { key: "faq_feature_multi_currency" },
        { key: "faq_feature_variants" },
        { key: "faq_feature_bundles" },
        { key: "faq_feature_stock_alerts" },
        { key: "faq_feature_purchase_orders" },
        { key: "faq_feature_suppliers" },
        { key: "faq_feature_customers" },
        { key: "faq_feature_loyalty" },
        { key: "faq_feature_employees" },
        { key: "faq_feature_permissions" },
        { key: "faq_feature_reports" },
        { key: "faq_feature_profit" },
        { key: "faq_feature_expenses" },
      ],
      restaurantNeeds: [
        { key: "faq_restaurant_table" },
        { key: "faq_restaurant_kds" },
        { key: "faq_restaurant_qr" },
        { key: "faq_restaurant_booking" },
        { key: "faq_restaurant_split" },
        { key: "faq_restaurant_stations" },
        { key: "faq_restaurant_modifications" },
        { key: "faq_restaurant_delivery" },
      ],
      advancedRequirements: [
        { key: "faq_advanced_multi_branch" },
        { key: "faq_advanced_api" },
        { key: "faq_advanced_receipt_design" },
        { key: "faq_advanced_payment_gateway" },
        { key: "faq_advanced_logistics" },
        { key: "faq_advanced_accounting" },
        { key: "faq_advanced_ecommerce" },
        { key: "faq_advanced_wholesale" },
      ],
      paymentStructures: [
        { key: "faq_payment_monthly" },
        { key: "faq_payment_yearly" },
        { key: "faq_payment_one_time" },
        { key: "faq_payment_plan" },
        { key: "faq_payment_undecided" },
      ],
      budgetAllocations: [
        { key: "faq_budget_alloc_under_300k" },
        { key: "faq_budget_alloc_300k_500k" },
        { key: "faq_budget_alloc_500k_750k" },
        { key: "faq_budget_alloc_750k_1m" },
        { key: "faq_budget_alloc_over_1m" },
        { key: "faq_budget_alloc_flexible" },
      ],
      decisionTimelines: [
        { key: "faq_timeline_urgent" },
        { key: "faq_timeline_soon" },
        { key: "faq_timeline_planning" },
        { key: "faq_timeline_researching" },
        { key: "faq_timeline_long_term" },
      ],
      decisionMakers: [
        { key: "faq_maker_self" },
        { key: "faq_maker_partner" },
        { key: "faq_maker_management" },
        { key: "faq_maker_committee" },
      ],
      implementationTimelines: [
        { key: "faq_impl_immediate" },
        { key: "faq_impl_1_week" },
        { key: "faq_impl_2_4_weeks" },
        { key: "faq_impl_flexible" },
        { key: "faq_impl_coordinating" },
      ],
      trainingRequirements: [
        { key: "faq_training_self_service" },
        { key: "faq_training_remote" },
        { key: "faq_training_onsite" },
        { key: "faq_training_ongoing" },
        { key: "faq_training_dedicated" },
      ],
      supportLevels: [
        { key: "faq_support_basic" },
        { key: "faq_support_standard" },
        { key: "faq_support_priority" },
        { key: "faq_support_premium" },
        { key: "faq_support_enterprise" },
      ],
      dataMigrationOptions: [
        { key: "faq_migration_fresh" },
        { key: "faq_migration_small" },
        { key: "faq_migration_significant" },
        { key: "faq_migration_complex" },
        { key: "faq_migration_historical" },
      ],
      contactMethods: [
        { key: "faq_contact_method_phone" },
        { key: "faq_contact_method_whatsapp" },
        { key: "faq_contact_method_email" },
        { key: "faq_contact_method_facebook" },
        { key: "faq_contact_method_in_person" },
      ],
      bestTimes: [
        { key: "faq_time_morning" },
        { key: "faq_time_afternoon" },
        { key: "faq_time_evening" },
        { key: "faq_time_anytime" },
      ],
      preferredLanguages: [
        { key: "faq_lang_lao" },
        { key: "faq_lang_english" },
        { key: "faq_lang_thai" },
        { key: "faq_lang_bilingual" },
      ],
      referralSources: [
        { key: "faq_source_facebook" },
        { key: "faq_source_tiktok" },
        { key: "faq_source_youtube" },
        { key: "faq_source_google" },
        { key: "faq_source_referral" },
        { key: "faq_source_saw_business" },
        { key: "faq_source_trade_show" },
      ],
    };
  },
  computed: {
    sections() {
      return [
        { icon: "fal fa-store", titleKey: "faq_section_business_profile", blocks: [
          { titleKey: "faq_business_type_title", descKey: "faq_business_type_description", items: this.businessTypes },
          { titleKey: "faq_business_stage_title", descKey: "faq_business_stage_description", items: this.businessStages },
          { titleKey: "faq_business_hours_title", descKey: "faq_business_hours_description", items: this.businessHours },
        ] },
        { icon: "fal fa-chart-line", titleKey: "faq_section_operations", blocks: [
          { titleKey: "faq_product_volume_title", descKey: "faq_product_volume_description", items: this.productVolumes },
          { titleKey: "faq_transaction_volume_title", descKey: "faq_transaction_volume_description", items: this.transactionVolumes },
          { titleKey: "faq_transaction_value_title", descKey: "faq_transaction_value_description", items: this.transactionValues },
          { titleKey: "faq_revenue_range_title", descKey: "faq_revenue_range_description", items: this.revenueRanges },
        ] },
        { icon: "fal fa-users", titleKey: "faq_section_team_infrastructure", blocks: [
          { titleKey: "faq_team_size_title", descKey: "faq_team_size_description", items: this.teamSizes },
          { titleKey: "faq_locations_title", descKey: "faq_locations_description", items: this.locations },
          { titleKey: "faq_technical_proficiency_title", descKey: "faq_technical_proficiency_description", items: this.technicalLevels },
          { titleKey: "faq_internet_connectivity_title", descKey: "faq_internet_connectivity_description", items: this.connectivityOptions },
        ] },
        { icon: "fal fa-laptop", titleKey: "faq_section_technology", blocks: [
          { titleKey: "faq_existing_hardware_title", descKey: "faq_existing_hardware_description", items: this.hardwareOptions },
          { titleKey: "faq_preferred_platform_title", descKey: "faq_preferred_platform_description", items: this.platformOptions },
          { titleKey: "faq_hardware_budget_title", descKey: "faq_hardware_budget_description", items: this.budgetOptions },
        ] },
        { icon: "fal fa-exclamation-triangle", titleKey: "faq_section_challenges", blocks: [
          { titleKey: "faq_primary_challenges_title", descKey: "faq_primary_challenges_description", items: this.challenges },
          { titleKey: "faq_record_keeping_title", descKey: "faq_record_keeping_description", items: this.recordKeepingMethods },
          { titleKey: "faq_pos_experience_title", descKey: "faq_pos_experience_description", items: this.posExperiences },
          { titleKey: "faq_reason_seeking_title", descKey: "faq_reason_seeking_description", items: this.seekingReasons },
        ] },
        { icon: "fal fa-cogs", titleKey: "faq_section_features", blocks: [
          { titleKey: "faq_essential_features_title", descKey: "faq_essential_features_description", items: this.essentialFeatures },
          { titleKey: "faq_restaurant_needs_title", descKey: "faq_restaurant_needs_description", items: this.restaurantNeeds },
          { titleKey: "faq_advanced_requirements_title", descKey: "faq_advanced_requirements_description", items: this.advancedRequirements },
        ] },
        { icon: "fal fa-money-bill-wave", titleKey: "faq_section_financial", blocks: [
          { titleKey: "faq_payment_structure_title", descKey: "faq_payment_structure_description", items: this.paymentStructures },
          { titleKey: "faq_budget_allocation_title", descKey: "faq_budget_allocation_description", items: this.budgetAllocations },
          { titleKey: "faq_decision_timeline_title", descKey: "faq_decision_timeline_description", items: this.decisionTimelines },
          { titleKey: "faq_decision_maker_title", descKey: "faq_decision_maker_description", items: this.decisionMakers },
        ] },
        { icon: "fal fa-headset", titleKey: "faq_section_implementation", blocks: [
          { titleKey: "faq_implementation_timeline_title", descKey: "faq_implementation_timeline_description", items: this.implementationTimelines },
          { titleKey: "faq_training_requirements_title", descKey: "faq_training_requirements_description", items: this.trainingRequirements },
          { titleKey: "faq_support_level_title", descKey: "faq_support_level_description", items: this.supportLevels },
          { titleKey: "faq_data_migration_title", descKey: "faq_data_migration_description", items: this.dataMigrationOptions },
        ] },
        { icon: "fal fa-address-card", titleKey: "faq_section_contact", blocks: [
          { titleKey: "faq_contact_info_title", descKey: "faq_contact_info_description", items: [
            { key: "faq_contact_business_name" },
            { key: "faq_contact_business_address" },
            { key: "faq_contact_phone" },
            { key: "faq_contact_email" },
          ] },
          { titleKey: "faq_preferred_contact_title", descKey: "faq_preferred_contact_description", items: this.contactMethods },
          { titleKey: "faq_best_time_title", descKey: "faq_best_time_description", items: this.bestTimes },
          { titleKey: "faq_preferred_language_title", descKey: "faq_preferred_language_description", items: this.preferredLanguages },
        ] },
        { icon: "fal fa-info-circle", titleKey: "faq_section_additional", blocks: [
          { titleKey: "faq_how_heard_about_title", descKey: "faq_how_heard_about_description", items: this.referralSources },
        ] },
      ];
    },
    filteredSections() {
      const q = (this.searchQuery || "").trim().toLowerCase();
      if (!q) return this.sections;
      return this.sections.filter((s) => {
        const title = (this.$t(s.titleKey) || "").toLowerCase();
        if (title.includes(q)) return true;
        return s.blocks.some((b) =>
          (this.$t(b.titleKey) || "").toLowerCase().includes(q)
        );
      });
    },
  },
  methods: {
    toggle(si) {
      const i = this.openPanels.indexOf(si);
      if (i >= 0) this.openPanels.splice(i, 1);
      else this.openPanels.push(si);
    },
  },
};
</script>

<style scoped>
.wrap {
  max-width: 80rem;
  margin: 0 auto;
  padding: 0 1rem;
}
@media (min-width: 640px) {
  .wrap {
    padding: 0 1.5rem;
  }
}
@media (min-width: 1024px) {
  .wrap {
    padding: 0 2rem;
  }
}

.faq-main {
  flex: 1;
  width: 100%;
  max-width: 56rem;
  padding-top: 56px;
  padding-bottom: 64px;
}

.faq-head {
  text-align: center;
}
.faq-head__sub {
  margin: 16px auto 0;
  max-width: 28rem;
  font-weight: 500;
  color: var(--ps-slate-500);
}

.faq-search {
  position: relative;
  margin: 32px 0;
}
.faq-search__icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
}
.faq-search__input {
  padding-left: 44px;
}

.faq-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.faq-card {
  background: #fff;
  border: 1px solid #141021;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 2px 2px 0 0 #141021;
}

.faq-card__head {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 24px;
  background: transparent;
  border: none;
  text-align: left;
  font-weight: 900;
  font-size: 1rem;
  color: var(--ps-ink);
  cursor: pointer;
  transition: background-color 0.15s ease;
}
.faq-card__head:hover {
  background: var(--ps-cream);
}

.faq-card__icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: var(--ps-brand-soft);
  color: var(--ps-brand);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.faq-card__title {
  flex: 1;
}

.faq-card__chevron {
  color: var(--ps-slate-400);
  transition: transform 0.25s ease;
  flex-shrink: 0;
}
.faq-card__chevron--open {
  transform: rotate(180deg);
}

.faq-card__body {
  border-top: 1px dashed var(--ps-slate-300);
  padding: 8px 24px 24px;
}

.faq-block {
  margin-top: 20px;
}

.faq-block__title {
  margin: 0;
  font-weight: 900;
  font-size: 1.0625rem;
  color: var(--ps-ink);
}

.faq-block__desc {
  margin: 6px 0 0;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--ps-slate-600);
  line-height: 1.7;
}

.faq-block__list {
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 10px;
  grid-template-columns: 1fr;
}
@media (min-width: 640px) {
  .faq-block__list {
    grid-template-columns: 1fr 1fr;
  }
}

.faq-block__item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--ps-slate-700);
  line-height: 1.6;
}

.faq-block__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--ps-brand);
  margin-top: 6px;
  flex-shrink: 0;
}

.faq-block__item-desc {
  display: block;
  font-size: 0.8125rem;
  color: var(--ps-slate-400);
}

.faq-empty {
  text-align: center;
  padding: 40px 0;
  font-weight: 700;
  color: var(--ps-slate-400);
}

.faq-contact {
  margin-top: 40px;
  text-align: center;
}
</style>
